import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, Typography, useMediaQuery } from '@mui/material';
import JSONEditor, { JSONEditorOptions } from 'jsoneditor';
import 'jsoneditor/dist/jsoneditor.css';
import Tooltip from '../ui/tooltip';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';

type AddInvoiceProviderModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: { providerName: string; countryId: string; importFormat: string }) => void;
    isEdit?: { id: number, state: boolean };
};

const AddInvoiceProviderModal: React.FC<AddInvoiceProviderModalProps> = ({ open, onClose, onConfirm, isEdit }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [providerName, setProviderName] = useState('');
    const [country, setCountry] = useState('');
    const [importFormat, setImportFormat] = useState('');
    const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
    const [errors, setErrors] = useState<{ providerName?: string; country?: string; importFormat?: string }>({});
    const editorRef = useRef<HTMLDivElement>(null);
    const editorInstanceRef = useRef<JSONEditor | null>(null);

    const [fetchCountries, setFetchCountries] = useApi();
    const [fetchDetails, setFetchDetails] = useApi();

    useEffect(() => {
        if (!open) return;
        const { url, method } = buildApiRequestConfig(routes.country.fetch);
        setFetchCountries({ url, method });
    }, [open, setFetchCountries]);

    useEffect(() => {
        if (!open) return;

        const timer = setTimeout(() => {
            if (editorRef.current && !editorInstanceRef.current) {
                const options: JSONEditorOptions = {
                    mode: 'code',
                    indentation: 2,
                    search: false,
                    enableSort: false,
                    enableTransform: false,
                };
                try {
                    let initialValue: any = {};
                    if (importFormat) {
                        initialValue = JSON.parse(importFormat);
                    }
                    editorInstanceRef.current = new JSONEditor(editorRef.current, options, initialValue);

                    if (editorInstanceRef.current) {
                        setTimeout(() => {
                            const textarea = editorRef.current?.querySelector('textarea');
                            if (textarea) {
                                textarea.focus();
                            }
                        }, 100);
                    }
                } catch (e) {
                    editorInstanceRef.current = new JSONEditor(editorRef.current, options, {});
                }
            }
        }, 0);

        return () => {
            clearTimeout(timer);
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy();
                editorInstanceRef.current = null;
            }
        };
    }, [open, isEdit?.state, importFormat]);

    useEffect(() => {
        return () => {
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy();
                editorInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const { data, isLoading } = fetchCountries;
        if (data && !isLoading) {
            setCountryOptions(data.map((country: any) => ({
                value: country.id,
                label: country.country
            })));
        }
    }, [fetchCountries]);

    useEffect(() => {
        if (isEdit?.state && isEdit?.id) {
            const { url, method } = buildApiRequestConfig(routes.invoiceProviders.fetchById, [{ param: 'id', value: isEdit.id }]);
            setFetchDetails({ url, method });
        } else {
            setErrors({});
            setProviderName('');
            setCountry('');
            setImportFormat('');
        }
    }, [isEdit, setFetchDetails]);

    useEffect(() => {
        const { data, isLoading } = fetchDetails;
        if (data && !isLoading) {
            setProviderName(data.providerName);
            setCountry(data.countryId);
            setImportFormat(data.importFormat);
            if (editorInstanceRef.current) {
                try {
                    const parsedFormat = JSON.parse(data.importFormat);
                    editorInstanceRef.current.set(parsedFormat);
                } catch (e) {
                    editorInstanceRef.current.set({});
                }
            }
        }
    }, [fetchDetails]);

    const handleConfirm = () => {
        const newErrors: { providerName?: string; country?: string; importFormat?: string } = {};
        if (!providerName) newErrors.providerName = 'Provider name is required';
        if (!country) newErrors.country = 'Country is required';

        let jsonFormatString = '';
        if (editorInstanceRef.current) {
            try {
                const editorValue = editorInstanceRef.current.get();
                jsonFormatString = JSON.stringify(editorValue);
                if (!jsonFormatString || jsonFormatString === '{}') {
                    newErrors.importFormat = 'Import format cannot be empty';
                }
            } catch (e) {
                newErrors.importFormat = 'Invalid JSON format';
            }
        } else {
            newErrors.importFormat = 'Import format is required';
        }

        if (!newErrors.providerName && !newErrors.country && !newErrors.importFormat) {
            onConfirm({ providerName, countryId: country, importFormat: jsonFormatString });
            setProviderName('');
            setCountry('');
            setImportFormat('');
            if (editorInstanceRef.current) {
                editorInstanceRef.current.set({});
            }
        } else {
            setErrors(newErrors);
        }
    };

    const content = (
        <>
            <DialogTitle>
                <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{isEdit?.state ? 'Edit Provider' : 'Add New Provider'}</Typography>
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    <Box>
                        <Typography fontSize={14} color='var(--color-text-muted)'>
                            Provider name <span style={{ color: '#ff5252' }}>*</span>
                        </Typography>
                        <TextField
                            placeholder="Enter provider name"
                            value={providerName}
                            onChange={e => setProviderName(e.target.value)}
                            size="small"
                            fullWidth
                            error={!!errors.providerName}
                            helperText={errors.providerName}
                            InputProps={{ style: { borderRadius: 8 } }}
                            inputProps={{ maxLength: 64 }}
                        />
                    </Box>
                    <Box>
                        <Typography fontSize={14} color='var(--color-text-muted)'>
                            Country <span style={{ color: '#ff5252' }}>*</span>
                        </Typography>
                        <Dropdown
                            value={country}
                            onChange={setCountry}
                            options={countryOptions}
                            height={40}
                            error={!!errors.country}
                            helperText={errors.country}
                        />
                    </Box>
                    <Box>
                        <Box display="flex" alignItems="center">
                            <Typography fontSize={14} color='var(--color-text-muted)'>
                                Import Format (JSON) <span style={{ color: '#ff5252' }}>*</span>
                            </Typography>
                            <Tooltip 
                                message={
                                    <span style={{ fontStyle: 'italic' }}>
                                        Define the column structure in JSON format.<br />
                                        Example: {`{"invoiceNumber": "InvoiceNumber"}`}
                                    </span>
                                }
                            />
                        </Box>
                        <Box
                            sx={{
                                border: errors.importFormat ? '1px solid #f44336' : '1px solid #ccc',
                                borderRadius: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                zIndex: 1,
                                '& .jsoneditor': {
                                    borderRadius: '8px',
                                    border: 'none',
                                },
                                '& .jsoneditor-menu': {
                                    backgroundColor: 'var(--color-primary)',
                                    borderBottom: '1px solid #ddd',
                                    zIndex: 2,
                                },
                                '& .jsoneditor-modes button.selected': {
                                    backgroundColor: 'var(--color-primary)',
                                    color: '#fff',
                                },
                                '& textarea': {
                                    fontFamily: 'monospace',
                                    fontSize: '8px',
                                    zIndex: 1,
                                }
                            }}
                            onClick={() => {
                                const textarea = editorRef.current?.querySelector('textarea');
                                if (textarea) textarea.focus();
                            }}
                        >
                            <div
                                ref={editorRef}
                                style={{
                                    height: '200px',
                                    borderRadius: '8px',
                                    width: '100%',
                                }}
                            />
                        </Box>
                        {errors.importFormat && (
                            <Typography sx={{ fontSize: 12, color: '#f44336', mt: 0.5 }}>
                                {errors.importFormat}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} variant="contained" style={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 8, fontWeight: 600, boxShadow: 'none' }}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 8, fontWeight: 600, boxShadow: 'none' }}>{isEdit?.state ? 'Save Changes' : 'Add Provider'}</Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} heightVh={90}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, background: 'var(--color-page-background)' } }}>
            {content}
        </Dialog>
    );
};

export default AddInvoiceProviderModal;
